import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AXIS, formatTooltipValue, iAggregateChartProps, timeLabel } from "./chartAxis";

/**
 * Aggregate mail waiting over the window.
 *
 * Backlog is a gauge, so this shows a level, not a rate — which is why it is
 * a separate chart from throughput rather than a third line on it. Contract
 * §2.6 forbids summing a gauge across time; summing across tenants at one
 * instant, which is what the aggregate does, is fine.
 *
 * `connectNulls` is false for the same reason as the throughput chart: a
 * bucket nobody measured is a gap, not a level of zero.
 */
function BacklogChart({ data, width, height = 74 }: iAggregateChartProps) {
    if (data.points.length === 0) {
        return <div className="mp-chart-empty">No backlog reported in this window.</div>;
    }

    // Fed straight in, undownsampled — see chartAxis.ts for why.
    const points = data.points;

    const chart = (
        <AreaChart
            data={points}
            width={width}
            height={height}
            margin={{ top: 4, right: 6, bottom: 0, left: 0 }}
        >
            <CartesianGrid stroke="#f2f2f2" />
            {/* type="number" + dataMin/dataMax preserves time-proportional spacing when the fan-out drops a bucket. */}
            <XAxis
                dataKey="at"
                type="number"
                domain={["dataMin", "dataMax"]}
                tickFormatter={timeLabel}
                {...AXIS}
            />
            <YAxis
                width={34}
                {...AXIS}
            />
            <Tooltip
                labelFormatter={label => timeLabel(Number(label))}
                formatter={formatTooltipValue}
            />
            <Area
                type="monotone"
                dataKey="backlog"
                name="Waiting"
                stroke="#c0392b"
                strokeWidth={1.5}
                fill="#c0392b"
                fillOpacity={0.07}
                dot={false}
                connectNulls={false}
            />
        </AreaChart>
    );

    if (width !== undefined) {
        return chart;
    }
    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            {chart}
        </ResponsiveContainer>
    );
}

export default BacklogChart;
